import { BehaviorSubject, Observable } from "rxjs";
import { REVIEW_DATE_TIME_KEYES, REVIEW_END_DATE_TIME_KEY, REVIEW_INCLUDE_TIME_KEY, REVIEW_START_DATE_TIME_KEY } from "./review-helper";

import { Injectable, OnDestroy } from "@angular/core";
import { ReviewFilter } from "./models/review-filter";
import { ReviewFilterItem } from "./models/review-filter-item";
import { HttpCommonService, LocalStoreManager, ShowToastrService } from "projects/sepidz-web-framework/src/lib/services";
import { DBkeys } from "projects/sepidz-web-framework/src/lib/services/db-keys";
import { ReviewReportModel } from "./filter-name/filter-name.model";
import { DialogType } from "projects/sepidz-web-framework/src/lib/models/dialog.enum";
import { take } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";

export declare type ValueType = "array" | "boolean" | "booleanList" | "joinedString" | "text" | "number";

type DefaultTypeFilter = {
  name: string;
  filters: any[];
};

@Injectable({
  providedIn: "root",
})
export class ReviewService implements OnDestroy {
  _DBkeys = DBkeys;
  valueType?: "array" | "boolean" | "joinedString" | "text" | "number";
  public startDate: Date = new Date();
  public endDate: Date = new Date();
  public endTime = "23:59";
  public startTime = "00:00";
  public ReportKey: string;
  oldFilter: ReviewFilter;
  public ReviewReportList: ReviewReportModel[] = [];
  startDate$: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);
  endDate$: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);
  public filterChangeSubject = new BehaviorSubject<ReviewFilter>(null);
  includeTime: boolean = false;

  public filterSubject = new BehaviorSubject<ReviewFilter>(<ReviewFilter>{
    issuer: "INITIALIZE",
    changedKeys: REVIEW_DATE_TIME_KEYES,
    items: [
      <ReviewFilterItem>{
        key: REVIEW_START_DATE_TIME_KEY,
        title: this.translate.instant("ClientApp.Review.FromDate"),
        persianKey: this.translate.instant("ClientApp.Review.FromDate"),
        value: this.startDate,
        isDataFilter: false,
      },
      <ReviewFilterItem>{
        key: REVIEW_END_DATE_TIME_KEY,
        title: this.translate.instant("ClientApp.Review.UntilDate"),
        persianKey: this.translate.instant("ClientApp.Review.UntilDate"),
        value: this.endDate,
        isDataFilter: false,
      },
      <ReviewFilterItem>{
        key: REVIEW_INCLUDE_TIME_KEY,
        title: this.translate.instant("ClientApp.Review.IncludeClockFilter"),
        persianKey: this.translate.instant("ClientApp.Review.IncludeClockFilter"),
        value: false,
        isDataFilter: false,
      },
    ],
  });

  gridDefaultFilters: DefaultTypeFilter[] = [];

  constructor(
    protected localStorage: LocalStoreManager,
    private httpCommonService: HttpCommonService,
    private sts: ShowToastrService,
    private translate: TranslateService
  ) {}

  ngOnDestroy() {
    this.httpCommonService = undefined;
  }
  public get filter$(): Observable<ReviewFilter> {
    const filter = this.filterSubject.getValue();
    let item = filter.items.find((o) => o.key === REVIEW_START_DATE_TIME_KEY);
    if (item && !item.value) item.value = this.startDate;
    let item2 = filter.items.find((o) => o.key === REVIEW_END_DATE_TIME_KEY);
    if (item2 && !item2.value) item2.value = this.endDate;
    this.filterSubject.next(filter);

    return this.filterSubject.asObservable();
  }

  public removeFilter(issuer: string, key: string, values: any[], valueType?: ValueType, shouldUpdateFilterSubject?: boolean) {
    const filter = this.filterSubject.getValue();
    const items = filter.items.filter(
      (item) => !(item.key === key && (valueType === "number" || valueType === "text" || values.some((value) => value === item.value)))
    );

    this.applyFilter(items, issuer, [key], shouldUpdateFilterSubject);
    if (shouldUpdateFilterSubject) this.filterChangeSubject.next(filter);
    this.localStorage.savePermanentData(filter, this._DBkeys.REVIEW_FILTER_INFO);
  }

  public addFilter(issuer: string, items: ReviewFilterItem[]) {
    const filter = this.filterSubject.getValue();
    items.forEach((x) => {
      if ((x.valueType === "number" || x.valueType === "text" || x.valueType === "array") && x.value) {
        let item = filter.items.find((o) => o.key === x.key);
        if (item) {
          item.value = x.value;
        }
        this.filterSubject.next(filter);
      }
    });
    const newItems = items.filter((item) => !filter.items.some((o) => o.key === item.key && o.value === item.value));
    this.applyFilter(
      filter.items.concat(newItems),
      issuer,
      newItems.map((i) => i.key)
    );
    this.filterChangeSubject.next(filter);
    this.localStorage.savePermanentData(filter, this._DBkeys.REVIEW_FILTER_INFO);
  }
  public applyDateTimeFilter(issuer: string, startValue: any, endValue: any, includeTime: boolean) {
    const filter = this.filterSubject.getValue();
    if (
      startValue?.toString() !== filter?.items?.find((f) => f.key === "StartDate")?.value?.toString() ||
      endValue?.toString() !== this.oldFilter?.items?.find((f) => f.key === "EndDate")?.value?.toString() ||
      includeTime !== filter?.items?.find((f) => f.key === "IncludeTime")?.value
    ) {
      setTimeout(() => {
        this.filterChangeSubject.next(filter);
      }, 200);
    }

    const start = filter.items.find((item) => item.key === REVIEW_START_DATE_TIME_KEY);
    start.value = startValue;

    const end = filter.items.find((item) => item.key === REVIEW_END_DATE_TIME_KEY);
    end.value = endValue;

    const includeTimeItem = filter.items.find((item) => item.key === REVIEW_INCLUDE_TIME_KEY);
    includeTimeItem.value = includeTime;

    this.applyFilter(filter.items, issuer, REVIEW_DATE_TIME_KEYES);
  }

  private applyFilter(items: ReviewFilterItem[], issuer: string, changedKeys: string[], shouldUpdateFilterSubject = true) {
    if (!changedKeys.length) {
      return;
    }
    const filter = this.filterSubject.getValue();
    filter.issuer = issuer;
    filter.changedKeys = changedKeys.filter((c) => changedKeys.indexOf(c) >= 0);
    filter.items = items;
    this.oldFilter = Object.assign({}, filter);
    if (shouldUpdateFilterSubject) {
      this.filterSubject.next(filter);
    }
  }
  /* Add For GetCustom Review Report */
  getReviewReport() {
    this.httpCommonService
      .getList("ReviewReport/GetAllByKey/" + this.ReportKey)
      .pipe(take(1))
      .subscribe(
        (x: ReviewReportModel[]) => {
          const val = new ReviewReportModel();
          this.ReviewReportList = x;
        },
        (error) => {
          console.timeLog(error);
        }
      );
  }
  /* Set GetCustom Review Report */
  setReviewReport($event: any) {
    const res = JSON.parse($event.DataValue);
    this.filterSubject.next(res);
  }
  /* Delete GetCustom Review Report */
  deleteReviewReport(id: number) {
    this.sts.showDialog(
      this.translate.instant("ClientApp.Review.LikeDeleteReport"),
      this.translate.instant("ClientApp.Review.Warning"),
      DialogType.confirm,
      () => {
        this.httpCommonService
          .delete("ReviewReport/DeleteData", id)
          .pipe(take(1))
          .subscribe(
            (x: ReviewReportModel[]) => {
              const filter = this.filterSubject.getValue();
              filter.items = filter.items.splice(0, 3);
              this.filterSubject.next(filter);
              this.getReviewReport();
            },
            (error) => {
              console.timeLog(error);
            }
          );
      }
    );
  }
}
